import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {AppsGroups} from "@app/netscope/model/data-models";
import {
  AppsGroupRef,
  AppsGroupsEditing,
  MemberSource,
  PatternKind,
  ResultingMember
} from "@app/netscope/netscope-apps-groups-viewer/apps-groups-editing";

export type MembersFilter = "all" | MemberSource;

// A resource offered to be picked; the combobox filters its options on their search text
export class PickSuggestion {
  uuid: string;
  name: string;
  type: string;
  address: string;
  searchText: string;
}

export class IncludeCandidate {
  ref: AppsGroupRef;
  fileLabel: string;
  countLabel: string;
  searchText: string;
}

// A row of the members table
export class MemberRow {
  uuid: string;
  name: string;
  source: MemberSource;
  typeLabel: string;
  why: string;
}

// Section of the editor a new group opens on, after the way its members are recognised
export type EditorSection = "picked" | "rules" | "groups";

const SUGGESTIONS_COUNT = 6;
const RULE_SAMPLE_COUNT = 12;
const DRAFT_SAMPLE_COUNT = 4;
const DRAFT_PREVIEW_DELAY_MS = 300;

/**
 * Editor of one apps group: its name and description, the three ways its members are defined (picked
 * resources, name rules, other groups), and the members that result, each with the reason it is there.
 */
@Component({
  standalone: false,
  selector: 'app-apps-group-editor',
  templateUrl: './apps-group-editor.component.html',
  styleUrls: ['./apps-group-editor.component.css']
})
export class AppsGroupEditorComponent implements OnChanges {

  @Input() editing: AppsGroupsEditing;
  @Input() group: AppsGroups;
  // Number of flows descriptions using the group
  @Input() descriptionsCount: number = 0;
  // Files of the user, where a group of a generated file can be duplicated
  @Input() userFiles: string[] = [];
  // Section to bring into view, for a group just created
  @Input() focusSection: EditorSection;

  @Output() showOnMap: EventEmitter<AppsGroups> = new EventEmitter();
  @Output() seeFlows: EventEmitter<AppsGroups> = new EventEmitter();
  @Output() openGroup: EventEmitter<AppsGroupRef> = new EventEmitter();
  @Output() duplicate: EventEmitter<{ group: AppsGroups, file: string }> = new EventEmitter();
  @Output() deleted: EventEmitter<AppsGroups> = new EventEmitter();

  nameDraft: string;
  nameError: string;

  pickQuery = "";
  pickSelection: PickSuggestion;
  suggestions: PickSuggestion[] = [];

  patternKind: PatternKind = "include";
  patternDraft = "";
  draftPreview: string;
  private draftTimer;
  private draftRequestId = 0;

  groupSelection: IncludeCandidate;
  confirmDelete = false;

  membersFilter: MembersFilter = "all";

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group']) {
      this.nameDraft = this.group?.name;
      this.nameError = undefined;
      this.pickQuery = "";
      this.suggestions = [];
      this.patternDraft = "";
      this.draftPreview = undefined;
      this.confirmDelete = false;
      this.membersFilter = "all";
    }
    if (changes['focusSection'] && this.focusSection != undefined) {
      // The section is rendered with the group: it is reached once the view is updated
      setTimeout(() => this.bringSectionIntoView(this.focusSection));
    }
  }

  get readOnly(): boolean {
    return this.editing.readOnly;
  }

  private bringSectionIntoView(section: EditorSection) {
    let target = document.getElementById(`apps-group-editor-${section}`);
    target?.scrollIntoView({behavior: "smooth", block: "start"});
    let field = target?.querySelector(".source-footer input") as HTMLElement;
    field?.focus({preventScroll: true});
  }

  // ---------------------------------------------------------------- Name and description

  onNameInput = (name: string) => {
    this.nameDraft = name;
    this.nameError = this.editing.nameError(this.group, name);
    if (this.nameError == undefined) {
      this.editing.rename(this.group, name);
    }
  }

  onDescriptionInput = (description: string) => {
    this.editing.setDescription(this.group, description);
  }

  get parents() {
    return this.editing.parentsOf(this.group);
  }

  get usedLabel(): string {
    let count = this.descriptionsCount ?? 0;
    if (count == 0) {
      return "Not used in flows descriptions yet";
    }
    return count == 1 ? "Used in 1 flows description" : `Used in ${count} flows descriptions`;
  }

  // ---------------------------------------------------------------- Members

  get members(): ResultingMember[] {
    return this.editing.members(this.group);
  }

  countOf = (source: MemberSource): number => {
    return this.members.filter((member) => member.source == source).length;
  }

  // Width of the share of the members coming from a source, in the bars
  percentOf = (source: MemberSource): string => {
    let total = this.members.length;
    return total == 0 ? "0%" : `${(100 * this.countOf(source) / total).toFixed(2)}%`;
  }

  get totalExplain(): string {
    let parts = [];
    if (this.countOf("picked") > 0) {
      parts.push(`${this.countOf("picked")} picked`);
    }
    if (this.countOf("rule") > 0) {
      parts.push(`${this.countOf("rule")} by name rules`);
    }
    if (this.countOf("included") > 0) {
      parts.push(`${this.countOf("included")} from other groups`);
    }
    return parts.length > 0 ? `= ${parts.join(" + ")}` : "Empty for now";
  }

  // The rows are kept while the members and the filter stay the same, for the table to keep its state
  private rowsKey: string;
  private rows: MemberRow[] = [];

  get filteredMembers(): MemberRow[] {
    let key = `${this.editing.revision}/${this.group.name}/${this.membersFilter}`;
    if (key != this.rowsKey) {
      this.rowsKey = key;
      this.rows = this.members
        .filter((member) => this.membersFilter == "all" || member.source == this.membersFilter)
        .map((member) => ({
          uuid: member.uuid,
          name: member.name,
          source: member.source,
          typeLabel: this.typeLabel(member.type),
          why: this.whyLabel(member),
        }));
    }
    return this.rows;
  }

  trackMember = (index: number, row: MemberRow) => row.uuid;

  get membersFilters(): { key: MembersFilter, label: string }[] {
    return [
      {key: "all", label: `All ${this.members.length}`},
      {key: "picked", label: `Picked ${this.countOf("picked")}`},
      {key: "rule", label: `Name rules ${this.countOf("rule")}`},
      {key: "included", label: `Other groups ${this.countOf("included")}`},
    ];
  }

  whyLabel = (member: ResultingMember): string => {
    switch (member.source) {
      case "picked":
        return "picked";
      case "rule":
        return "name rule";
      default:
        return `via ${member.via}`;
    }
  }

  // Short label of the kind of a resource, shown in front of its name
  typeLabel = (type: string): string => {
    switch (type) {
      case "vm":
        return "VM";
      case "host":
        return "Host";
      case "network":
        return "Net";
      case "external network":
        return "Ext net";
      case "external ip":
        return "Ext IP";
      case "ip":
        return "IP";
      default:
        return "Other";
    }
  }

  // ---------------------------------------------------------------- Picked resources

  get picked(): { uuid: string, name: string, type: string }[] {
    return (this.group.vms ?? []).map((vm) => ({
      uuid: vm.uuid,
      name: this.editing.inventory.resourceLabel(vm.uuid),
      type: this.editing.inventory.memberType(vm.uuid),
    }));
  }

  onPickQuery = (query: string) => {
    this.pickQuery = query ?? "";
    this.suggestions = this.findSuggestions();
  }

  /**
   * Resources matching what the user types, on their name, address or uuid, the ones already picked aside.
   * An address or a network the user types is offered first when no resource of the inventory holds it.
   */
  private findSuggestions(): PickSuggestion[] {
    let query = this.pickQuery.trim().toLowerCase();
    if (query === "") {
      return [];
    }

    let pickedUuids = new Set((this.group.vms ?? []).map((vm) => vm.uuid));
    let matches = (text: string) => text != undefined && text.toLowerCase().includes(query);
    let resources = this.editing.inventory.selectableResources
      .filter((resource) => matches(resource.name) || matches(resource.address) || matches(resource.uuid));

    let typed = this.editing.inventory.typedAddressResource(this.pickQuery);
    if (typed != undefined && !resources.some((resource) => resource.uuid == typed.uuid)) {
      resources = [typed].concat(resources);
    }

    return resources
      .filter((resource) => !pickedUuids.has(resource.uuid))
      .slice(0, SUGGESTIONS_COUNT)
      .map((resource) => ({
        uuid: resource.uuid,
        name: resource.name,
        type: resource.resourceType,
        address: resource.address,
        // What matched, so that the combobox keeps the option
        searchText: [resource.name, resource.address, resource.uuid].filter((text) => text != undefined).join(" "),
      }));
  }

  onPickSelected = (suggestion: PickSuggestion) => {
    if (suggestion == undefined) {
      return;
    }
    this.pickSelection = suggestion;
    this.addPicked(suggestion.uuid);
    // The combobox is emptied once it is done with the selection, for the next resource
    setTimeout(() => {
      this.pickSelection = undefined;
      this.onPickQuery("");
    });
  }

  addPicked = (uuid: string) => {
    this.editing.addPicked(this.group, uuid);
  }

  removePicked = (uuid: string) => {
    this.editing.removePicked(this.group, uuid);
  }

  // ---------------------------------------------------------------- Name rules

  patterns = (kind: PatternKind): string[] => {
    return this.editing.patterns(this.group, kind);
  }

  hitsLabel = (pattern: string): string => {
    let hits = this.editing.patternHits.get(pattern);
    if (hits == undefined) {
      return "…";
    }
    return hits == 1 ? "1 match" : `${hits} matches`;
  }

  get ruleMatchesLabel(): string {
    let count = this.editing.ruleMatchesCount(this.group);
    return count == undefined ? "matching…" : `${count} matched`;
  }

  // First resources the rules match, the rest being counted
  get ruleSample(): string[] {
    return this.members
      .filter((member) => member.source == "rule")
      .map((member) => member.name)
      .sort()
      .slice(0, RULE_SAMPLE_COUNT);
  }

  get ruleSampleMore(): string {
    let total = this.editing.ruleMatchesCount(this.group) ?? 0;
    let others = total - this.ruleSample.length;
    return others > 0 ? `and ${others} more` : "";
  }

  setPatternKind = (kind: PatternKind) => {
    this.patternKind = kind;
    this.onPatternDraft(this.patternDraft);
  }

  /**
   * Tells what the pattern being typed would do, once the user pauses: the resources it would bring in, or the
   * members it would leave out.
   */
  onPatternDraft = (draft: string) => {
    this.patternDraft = draft;
    clearTimeout(this.draftTimer);

    let pattern = draft.trim();
    if (pattern === "") {
      this.draftPreview = undefined;
      return;
    }

    let kind = this.patternKind;
    this.draftTimer = setTimeout(async () => {
      let requestId = ++this.draftRequestId;
      let preview = await this.editing.preview([pattern], []);
      if (requestId != this.draftRequestId) {
        return;
      }
      if (preview.failed) {
        this.draftPreview = `“${pattern}” could not be checked against the inventory.`;
        return;
      }
      if (preview.total == 0) {
        this.draftPreview = `“${pattern}” matches nothing in the inventory today. It will catch matching resources as they appear.`;
        return;
      }

      let names = (members: { name: string }[], count: number) => {
        let sample = members.slice(0, DRAFT_SAMPLE_COUNT).map((member) => member.name).join(", ");
        return `${count} ${count == 1 ? "resource" : "resources"}: ${sample}${count > DRAFT_SAMPLE_COUNT ? "…" : ""}`;
      };

      if (kind == "include") {
        this.draftPreview = `“${pattern}” would add ${names(preview.members, preview.total)}`;
      } else {
        let matchedUuids = new Set((this.group.matched_vms ?? []).map((vm) => vm.uuid));
        let leftOut = preview.members.filter((member) => matchedUuids.has(member.uuid));
        this.draftPreview = leftOut.length == 0
          ? `“${pattern}” would leave out none of the resources the rules match today.`
          : `“${pattern}” would leave out ${names(leftOut, leftOut.length)}`;
      }
    }, DRAFT_PREVIEW_DELAY_MS);
  }

  addPattern = () => {
    if (this.patternDraft.trim() === "") {
      return;
    }
    this.editing.addPattern(this.group, this.patternKind, this.patternDraft);
    this.patternDraft = "";
    this.draftPreview = undefined;
    // A preview of the pattern may still be on its way: it would describe a pattern that is now a rule
    clearTimeout(this.draftTimer);
    this.draftRequestId++;
  }

  removePattern = (kind: PatternKind, pattern: string) => {
    this.editing.removePattern(this.group, kind, pattern);
  }

  get patternsError(): string {
    return this.editing.patternsError(this.group);
  }

  // ---------------------------------------------------------------- Other groups

  get included(): { ref: AppsGroupRef, missing: boolean }[] {
    return (this.group.included_apps_groups ?? []).map((included) => {
      let ref = this.editing.refOf(included, this.editing.file);
      return {ref: ref, missing: this.editing.find(ref) == undefined};
    });
  }

  includedWhere = (ref: AppsGroupRef, missing: boolean): string => {
    if (missing) {
      return "not found";
    }
    let count = this.editing.members(this.editing.find(ref), ref.file).length;
    let where = ref.file == this.editing.file ? "this file" : ref.file;
    return `${where} · ${count} ${count == 1 ? "member" : "members"}`;
  }

  private candidatesKey: string;
  private candidatesList: IncludeCandidate[] = [];
  private blockedList: string[] = [];

  // Groups that can be included, kept while nothing changes since the combobox tracks them
  get candidates(): IncludeCandidate[] {
    this.updateCandidates();
    return this.candidatesList;
  }

  // Groups that cannot be included, since they include this one already
  get blockedCandidates(): string[] {
    this.updateCandidates();
    return this.blockedList;
  }

  private updateCandidates() {
    let key = `${this.editing.revision}/${this.group.name}`;
    if (key == this.candidatesKey) {
      return;
    }
    this.candidatesKey = key;

    let all = this.editing.includeCandidates(this.group);
    this.blockedList = all.filter((candidate) => candidate.blocked).map((candidate) => candidate.ref.name);
    this.candidatesList = all.filter((candidate) => !candidate.blocked).map((candidate) => {
      let count = this.editing.members(this.editing.find(candidate.ref), candidate.ref.file).length;
      let fileLabel = candidate.ref.file == this.editing.file ? "this file" : candidate.ref.file;
      return {
        ref: candidate.ref,
        fileLabel: fileLabel,
        countLabel: `${count} ${count == 1 ? "member" : "members"}`,
        searchText: `${candidate.ref.name} ${fileLabel}`,
      };
    });
  }

  onGroupSelected = (candidate: IncludeCandidate) => {
    if (candidate == undefined) {
      return;
    }
    this.groupSelection = candidate;
    this.editing.include(this.group, candidate.ref);
    setTimeout(() => {
      this.groupSelection = undefined;
    });
  }

  removeInclusion = (ref: AppsGroupRef) => {
    this.editing.removeInclusion(this.group, ref);
  }

  // ---------------------------------------------------------------- Actions

  askDuplicate = (file: string) => {
    this.duplicate.emit({group: this.group, file: file});
  }

  get externalParentsCount(): number {
    return this.editing.externalParentsCount(this.group);
  }

  deleteGroup = () => {
    this.confirmDelete = false;
    this.editing.deleteGroup(this.group);
    this.deleted.emit(this.group);
  }
}
